import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  Flex,
  FormLabel,
  Heading,
  Input,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FormEventHandler, useRef, useState } from "react";
import { useNavigate } from "react-router";

import AdminOrgSwitcher from "@/admin/components/AdminOrgSwitcher";
import { useAdminOrgCreateMutation } from "@/admin/data/useAdminOrgCreateMutation";
import { useAdminUserDeleteMutation } from "@/admin/data/useAdminUserDeleteMutation";
import { useAdminUsersSearch } from "@/admin/data/useAdminUsersSearch";
import { useCodeReferencesSyncMissingHashesMutation } from "@/admin/data/useCodeReferencesSyncMissingHashesMutation";
import AutocompleteSelect, {
  highlightStringMatch,
} from "@/common/components/Autocomplete/AutocompleteSelect";
import { AdminCreateOrgType, AdminUser } from "@reflag/shared/adminAPI";
import { AdminUrl } from "@reflag/shared/urls";

export default function AdminIndex() {
  const createOrgMutation = useAdminOrgCreateMutation();
  const deleteMutation = useAdminUserDeleteMutation();
  const toast = useToast();
  const navigate = useNavigate();
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const confirmDeleteDisclosure = useDisclosure();
  const cancelRef = useRef(null);
  const { data: usersData, isLoading: isLoadingUsers } =
    useAdminUsersSearch(userSearch);
  const users = usersData ?? [];
  const syncMissingHashesMutation = useCodeReferencesSyncMissingHashesMutation({
    onSuccess: ({ queuedCount }) => {
      toast({
        title: "Code scan sync jobs queued",
        description: `Queued ${queuedCount} ${queuedCount === 1 ? "connection" : "connections"}.`,
        status: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Unable to queue code scan sync jobs",
        description: error.data?.error?.message ?? "Unknown error",
        status: "error",
      });
    },
  });

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    createOrgMutation.mutate(
      Object.fromEntries(formData.entries()) as AdminCreateOrgType,
      {
        onSuccess(data) {
          navigate(AdminUrl(data));
        },
      },
    );
  };

  return (
    <Box as="main" marginX="auto" maxW="compactForm" padding={4}>
      <Flex direction="column" gap={4}>
        <Heading as="h1">Reflag Admin</Heading>
        <Text fontSize="lg">
          Select an organization to administrate, or create a new one with you
          as the first member
        </Text>

        <Box>
          <FormLabel htmlFor="admin-org-switcher">Go to organization</FormLabel>
          <AdminOrgSwitcher id="admin-org-switcher" size="lg" />
        </Box>

        <Divider />

        <Card p={4}>
          <Flex direction="column" gap={2}>
            <Text fontSize="sm">
              Queue code reference scan sync jobs for GitHub connections that
              are missing their latest scanned hash.
            </Text>
            <ButtonGroup>
              <Button
                isLoading={syncMissingHashesMutation.isPending}
                onClick={() => syncMissingHashesMutation.mutate()}
              >
                Queue code scan sync jobs
              </Button>
            </ButtonGroup>
          </Flex>
        </Card>

        <Divider />

        <form onSubmit={handleSubmit}>
          <Card p={4}>
            <Flex direction="column" gap={2}>
              <Box>
                <FormLabel htmlFor="org-name">Organization name</FormLabel>
                <Input
                  autoComplete="off"
                  id="org-name"
                  maxLength={40}
                  minLength={1}
                  name="name"
                  placeholder="Acme Inc."
                  required
                />
              </Box>

              <ButtonGroup>
                <Button type="submit">Create new organization</Button>
              </ButtonGroup>
            </Flex>
          </Card>
        </form>

        <Divider />

        <Card p={4}>
          <Flex direction="column" gap={2}>
            <FormLabel>Delete user</FormLabel>
            <AutocompleteSelect<AdminUser>
              isLoadingSuggestions={isLoadingUsers}
              itemKeyProperty="id"
              itemRenderFn={(user, search) => (
                <Flex alignItems="center" gap={2} padding={1}>
                  <Text>{highlightStringMatch(user.name, search ?? "")}</Text>
                  <Text color="dimmed" fontSize="xs">
                    {highlightStringMatch(user.email, search ?? "")}
                  </Text>
                </Flex>
              )}
              placeholder="Search for a user"
              suggestions={users}
              value={null}
              onChange={(user) => {
                if (user) {
                  setSelectedUser(user);
                  confirmDeleteDisclosure.onOpen();
                }
              }}
              onSearchInput={setUserSearch}
            />
          </Flex>
        </Card>

        <AlertDialog
          isOpen={confirmDeleteDisclosure.isOpen}
          leastDestructiveRef={cancelRef}
          onClose={confirmDeleteDisclosure.onClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>Delete user</AlertDialogHeader>
              <AlertDialogBody align="flex-start" as={VStack} spacing={4}>
                <Text as="p">
                  Are you sure you want to permanently delete{" "}
                  <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
                  This will remove all their organization memberships, access
                  tokens, and API keys. This action cannot be undone.
                </Text>

                <Alert alignItems="flex-start" status="warning">
                  <AlertIcon />
                  <Text as="p">This action cannot be undone.</Text>
                </Alert>
              </AlertDialogBody>
              <AlertDialogFooter>
                <ButtonGroup>
                  <Button
                    ref={cancelRef}
                    variant="ghostDimmed"
                    onClick={confirmDeleteDisclosure.onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="red"
                    isLoading={deleteMutation.isPending}
                    onClick={() => {
                      if (!selectedUser) return;
                      deleteMutation.mutate(
                        { userId: selectedUser.id },
                        {
                          onSuccess() {
                            confirmDeleteDisclosure.onClose();
                            setSelectedUser(null);
                            toast({
                              title: "User deleted",
                              description: `${selectedUser.name} has been deleted.`,
                              status: "success",
                            });
                          },
                          onError(error) {
                            toast({
                              title: "Unable to delete user",
                              description:
                                error.data?.error?.message ?? "Unknown error",
                              status: "error",
                            });
                          },
                        },
                      );
                    }}
                  >
                    Delete
                  </Button>
                </ButtonGroup>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Flex>
    </Box>
  );
}
